import type {SanityModuleLooxReviews} from 'domains/modules';
import {useMatches} from '@remix-run/react';
import React from 'react';
import {I18nLocale} from '~/lib/type';

/**
 * Loox reviews — the "Gallery Carousel" (v2) widget: a visually focused
 * strip that shows only the photo/video of each review.
 *
 * History of the earlier breakage: the original code put a <script> in JSX
 * (React never runs those); a later attempt injected loox.js but the
 * &language param made loox serve a "please reinstall" stub; and loox.js's
 * auto-scan for the widget div does not fire reliably on this hydrated SPA.
 *
 * loox.js ultimately just mounts loox's own iframe
 * (loox.io/widget/<clientId>/v2/carousel) with the widget's data-* options
 * as query params and resizes it from a postMessage. We render that iframe
 * directly — no fragile auto-scan; the &language param is fine on the
 * iframe URL (only the loader script choked on it).
 *
 * Sort order: this carousel is always newest→oldest with photos/videos
 * prioritised — that's fixed by Loox for collection-wide widgets and is NOT
 * configurable (only the product-page reviews widget has a sort setting).
 * The carousel HTML embeds the reviews and Loox's CDN caches it for hours,
 * so a static iframe URL can lag well behind the newest review. loox.js
 * busts that with an `h=` param; we do the same, bucketed to the hour, so
 * new reviews surface within ~1h while still allowing in-hour caching. The
 * bucket is set after mount (not during SSR) to avoid a hydration mismatch.
 *
 * CLIENT_ID is stable per shop; if loox is reinstalled and it changes, read
 * it from loox.js (the `clientId` field).
 */
const CLIENT_ID = '4yxToKYeUo';
const FRAME_ID = 'hmk-loox-gallery';

// Loox is uninstalled: its widget only answers 'Loox reviews is uninstalled
// - reinstall to enable', so the iframe was a blank 320px box that still
// loaded Loox's admin-gtag.js. Set to true if Loox is reinstalled.
const LOOX_INSTALLED = false;

export default function LooxReviews(props: {module: SanityModuleLooxReviews}) {
  return LOOX_INSTALLED ? <LooxCarousel {...props} /> : null;
}

function LooxCarousel({module}: {module: SanityModuleLooxReviews}) {
  const [root] = useMatches();
  const selectedLocale = root.data.selectedLocale as I18nLocale;
  const lang = selectedLocale?.language === 'ZH_TW' ? 'zh-TW' : 'en';
  const [height, setHeight] = React.useState(320);
  const [failed, setFailed] = React.useState(false);
  // Hourly cache-buster, set after mount so SSR and first client render agree.
  const [hourBucket, setHourBucket] = React.useState<string | null>(null);

  React.useEffect(() => {
    setHourBucket(String(Math.floor(Date.now() / 3_600_000)));
  }, []);

  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== 'https://loox.io') return;
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (
          data?.type === 'resize' &&
          data?.frame === FRAME_ID &&
          typeof data.height === 'number' &&
          data.height > 0
        ) {
          setHeight(data.height);
        }
      } catch {
        // ignore non-JSON messages from other embeds
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (!module.active || failed) {
    return <></>;
  }

  const params = new URLSearchParams({
    slideType: 'gallery',
    frameId: FRAME_ID,
    language: lang,
    itemsPerView: '4',
    mobileItemsPerView: '2',
  });
  // Refresh the (hours-cached) carousel at most once an hour so newly added
  // reviews aren't hidden behind Loox's CDN cache.
  if (hourBucket) params.set('h', hourBucket);
  const src = `https://loox.io/widget/${CLIENT_ID}/v2/carousel?${params.toString()}`;

  return (
    <iframe
      title="Loox 顧客評價"
      src={src}
      onError={() => setFailed(true)}
      scrolling="no"
      style={{width: '100%', height, border: 0, overflow: 'hidden'}}
    />
  );
}
